import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { shared } from "../styles.js";
import { pb } from "../api/client.js";
import { updateProfile } from "../api/account.js";
import { humanError } from "../api/errors.js";

/**
 * settings-profile shows the account identity (email + permanent @handle) and
 * lets the user edit their display name (users.name). Saving refreshes the
 * cached auth record so the header avatar/initials update immediately.
 */
@customElement("settings-profile")
export class SettingsProfile extends LitElement {
  static override styles = [
    shared,
    css`
      .row {
        display: flex;
        justify-content: space-between;
        gap: var(--sp-3);
        padding: 8px 0;
        border-bottom: 1px solid var(--border);
        font-size: var(--fs-2);
      }
      .row:last-of-type {
        border-bottom: 0;
      }
      form {
        max-width: 360px;
        margin-top: var(--sp-3);
      }
    `,
  ];

  @state() private name = (pb.authStore.record?.name as string | undefined) ?? "";
  @state() private errorMsg = "";
  @state() private notice = "";
  @state() private submitting = false;

  private async submit(e: Event) {
    e.preventDefault();
    this.errorMsg = "";
    this.notice = "";
    this.submitting = true;
    try {
      await updateProfile(this.name.trim());
      this.notice = "Profile saved.";
    } catch (err) {
      this.errorMsg = humanError(err);
    } finally {
      this.submitting = false;
    }
  }

  override render() {
    const record = pb.authStore.record;
    const email = (record?.email as string | undefined) ?? "";
    const handle = (record?.handle as string | undefined) ?? "";
    return html`
      <div class="card">
        <h2>Profile</h2>
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        ${this.notice ? html`<div class="card" style="border-color:var(--pos)">${this.notice}</div>` : ""}
        <div class="row">
          <span class="muted">Email</span>
          <span class="mono">${email}</span>
        </div>
        <div class="row">
          <span class="muted">Handle <span class="faint">(permanent)</span></span>
          <a class="mono" href=${`/scopes/${handle}`}>@${handle}</a>
        </div>
        <form @submit=${this.submit}>
          <div class="field">
            <label for="display-name">Display name</label>
            <input
              id="display-name"
              .value=${this.name}
              @input=${(e: Event) => (this.name = (e.target as HTMLInputElement).value)}
            />
          </div>
          <button class="btn btn-primary" type="submit" ?disabled=${this.submitting}>
            ${this.submitting ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "settings-profile": SettingsProfile;
  }
}
